import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Input, Switch, message, Spin } from "antd";
import {
  CheckOutlined,
  ArrowLeftOutlined,
  BarChartOutlined,
  TeamOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import { MakeApiCall } from "../../../../apis";
import {
  SUB_USER_ROLES,
  FALLBACK_PAGES,
  FALLBACK_ROLE_DEFAULT_PAGES,
  applyModuleToggle,
  buildDefaultAccountAccess,
  buildModuleLabelMap,
  collectAssignablePermissionKeys,
  collectPageKeys,
  enrichPagesWithIcons,
  mapSellerAccessToAccountAccess,
} from "./constants";
import ModulePermissionTree from "./ModulePermissionTree";
import SubUserSummaryCard from "./SubUserSummaryCard";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ROLE_ICONS = {
  analyst: BarChartOutlined,
  manager: TeamOutlined,
  account_admin: SafetyCertificateOutlined,
};

export default function InviteSubUserForm({
  userData,
  editingUser = null,
  onBack,
  onSuccess,
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("analyst");
  const [subUserStatus, setSubUserStatus] = useState("active");
  const [accountAccess, setAccountAccess] = useState([]);
  const [loadingUser, setLoadingUser] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [subUserDetails, setSubUserDetails] = useState(null);
  const [permissionOptions, setPermissionOptions] = useState(null);

  const isEdit = !!editingUser?.id;

  const roles = permissionOptions?.roles?.length ? permissionOptions.roles : SUB_USER_ROLES;
  const pages = useMemo(() => {
    const source = permissionOptions?.pages?.length
      ? permissionOptions.pages
      : FALLBACK_PAGES;
    return enrichPagesWithIcons(source);
  }, [permissionOptions?.pages]);
  const roleDefaultPages = permissionOptions?.role_default_pages || FALLBACK_ROLE_DEFAULT_PAGES;
  const moduleLabelMap = useMemo(() => buildModuleLabelMap(pages), [pages]);
  const allPageKeys = useMemo(() => collectPageKeys(pages), [pages]);
  const assignableKeys = useMemo(
    () => collectAssignablePermissionKeys(roleDefaultPages),
    [roleDefaultPages]
  );

  const getRoleDefaultModules = useCallback(
    (roleKey) => [...(roleDefaultPages[roleKey] || [])],
    [roleDefaultPages]
  );

  useEffect(() => {
    let cancelled = false;

    const fetchPermissionOptions = async () => {
      setLoadingOptions(true);
      try {
        const response = await MakeApiCall(
          "sub-users/permission-options",
          "get",
          null,
          true,
          {},
          userData?.token
        );
        if (!cancelled && response?.status) {
          setPermissionOptions(response.data || null);
        }
      } catch {
        if (!cancelled) {
          message.error("Could not load permission options.");
        }
      } finally {
        if (!cancelled) setLoadingOptions(false);
      }
    };

    fetchPermissionOptions();

    return () => {
      cancelled = true;
    };
  }, [userData?.token]);

  useEffect(() => {
    if (!editingUser?.id) {
      setSubUserDetails(null);
      setSubUserStatus("active");
      return;
    }

    let cancelled = false;

    const fetchSubUser = async () => {
      setLoadingUser(true);
      try {
        const response = await MakeApiCall(
          `sub-users/${editingUser.id}`,
          "get",
          null,
          true,
          {},
          userData?.token
        );
        if (cancelled) return;

        if (response?.status) {
          const data = response.data || {};
          setSubUserDetails(data);
          setName(data.name || "");
          setEmail(data.email || "");
          setRole(data.sub_user_role || "analyst");
          setSubUserStatus(data.sub_user_status || "active");
        } else {
          message.warning(response?.message || "Could not load sub-user details.");
        }
      } catch {
        if (!cancelled) {
          message.error("Something went wrong loading sub-user details.");
        }
      } finally {
        if (!cancelled) setLoadingUser(false);
      }
    };

    fetchSubUser();

    return () => {
      cancelled = true;
    };
  }, [editingUser?.id, userData?.token]);

  const mergeSavedSellerAccess = useCallback((accounts, savedAccess = []) => {
    const editMap = new Map(savedAccess.map((entry) => [entry.seller_user_id, entry]));

    const merged = accounts.map((acc) => {
      const saved = editMap.get(acc.account_id);
      if (!saved) return acc;
      return {
        ...acc,
        enabled: !!saved.enabled,
        modules: saved.page_permissions || [],
      };
    });

    savedAccess.forEach((entry) => {
      if (merged.some((acc) => acc.account_id === entry.seller_user_id)) return;
      merged.push({
        account_id: entry.seller_user_id,
        account_name: entry.seller_name || entry.name || `Account ${entry.seller_user_id}`,
        marketplace: entry.marketplace || "",
        category: entry.category || "",
        enabled: !!entry.enabled,
        modules: entry.page_permissions || [],
      });
    });

    return merged;
  }, []);

  const loadSellerAccounts = useCallback(() => {
    setLoadingAccounts(true);
    try {
      const sellers = permissionOptions?.assignable_sellers || [];
      const mapped = buildDefaultAccountAccess(
        sellers.map((seller) => ({
          id: seller.id,
          name: seller.seller_name || seller.name,
          email: seller.email,
        }))
      );

      if (isEdit && subUserDetails?.seller_access?.length) {
        setAccountAccess(mergeSavedSellerAccess(mapped, subUserDetails.seller_access));
      } else {
        setAccountAccess(mapped);
      }
    } catch {
      if (isEdit && subUserDetails?.seller_access?.length) {
        setAccountAccess(mapSellerAccessToAccountAccess(subUserDetails.seller_access));
      } else {
        setAccountAccess([]);
      }
    } finally {
      setLoadingAccounts(false);
    }
  }, [permissionOptions?.assignable_sellers, isEdit, subUserDetails, mergeSavedSellerAccess]);

  useEffect(() => {
    if (loadingOptions) return;
    if (isEdit && !subUserDetails) return;
    loadSellerAccounts();
  }, [loadingOptions, isEdit, subUserDetails, loadSellerAccounts]);

  const handleRoleChange = (roleKey) => {
    setRole(roleKey);
    const defaults = getRoleDefaultModules(roleKey);
    if (!defaults.length) return;
    setAccountAccess((prev) =>
      prev.map((a) => (a.enabled ? { ...a, modules: defaults } : a))
    );
  };

  const toggleAccount = (accountId, enabled) => {
    const defaults = getRoleDefaultModules(role);
    setAccountAccess((prev) =>
      prev.map((a) =>
        a.account_id === accountId
          ? {
              ...a,
              enabled,
              modules: enabled ? (defaults.length ? defaults : a.modules) : [],
            }
          : a
      )
    );
  };

  const toggleModule = (accountId, moduleKey) => {
    setAccountAccess((prev) =>
      prev.map((a) => {
        if (a.account_id !== accountId || !a.enabled) return a;
        return {
          ...a,
          modules: applyModuleToggle(pages, a.modules, moduleKey, assignableKeys),
        };
      })
    );
  };

  const selectAllModules = (accountId) => {
    const keys = allPageKeys.length ? allPageKeys : getRoleDefaultModules(role);
    setAccountAccess((prev) =>
      prev.map((a) =>
        a.account_id === accountId && a.enabled ? { ...a, modules: [...keys] } : a
      )
    );
  };

  const validate = () => {
    if (!name.trim()) {
      message.warning("Please enter full name.");
      return false;
    }
    if (!email.trim() || !EMAIL_RE.test(email.trim())) {
      message.warning("Please enter a valid work email.");
      return false;
    }
    const enabled = accountAccess.filter((a) => a.enabled);
    if (enabled.length === 0) {
      message.warning("Enable at least one seller account.");
      return false;
    }
    const missingModules = enabled.some((a) => !a.modules?.length);
    if (missingModules) {
      message.warning("Select at least one module for each enabled account.");
      return false;
    }
    return true;
  };

  const buildSellerAccessPayload = () =>
    accountAccess
      .filter((a) => a.enabled)
      .map((a) => ({
        seller_user_id: a.account_id,
        enabled: true,
        page_permissions: a.modules,
      }));

  const buildInvitePayload = () => ({
    name: name.trim(),
    email: email.trim(),
    sub_user_role: role,
    seller_access: buildSellerAccessPayload(),
  });

  const buildUpdatePayload = () => ({
    name: name.trim(),
    sub_user_role: role,
    sub_user_status: subUserStatus,
    seller_access: buildSellerAccessPayload(),
  });

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    message.loading(isEdit ? "Updating…" : "Sending invite…", 0);
    try {
      const payload = isEdit ? buildUpdatePayload() : buildInvitePayload();
      const url = isEdit ? `sub-users/${editingUser.id}` : "sub-users";
      const method = isEdit ? "put" : "post";
      const response = await MakeApiCall(url, method, payload, true, {}, userData?.token);
      message.destroy();
      if (response?.status) {
        message.success(response?.message || (isEdit ? "Sub-user updated." : "Invite sent successfully."));
        onSuccess?.();
      } else {
        message.warning(response?.message || "Request failed. Please try again.");
      }
    } catch {
      message.destroy();
      message.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const accountInitial = (label) => (label?.trim()?.[0] || "A").toUpperCase();
  const enabledCount = useMemo(() => accountAccess.filter((a) => a.enabled).length, [accountAccess]);
  const isLoading = loadingUser || loadingAccounts || loadingOptions;

  return (
    <div className="mu-invite ">
      <div className="mu-invite__page-head">
        <button type="button" className="mu-invite__back-link" onClick={onBack}>
          <ArrowLeftOutlined />
          Back to team
        </button>
        <h1 className="mu-invite__page-title">
          {isEdit ? "Edit user" : "Invite a user"}
        </h1>
        <p className="mu-invite__page-subtitle">
          {isEdit
            ? "Update role, accounts, and module access for this team member."
            : "Give teammates access to seller accounts and modules — they receive an email invite."}
        </p>
      </div>

      <div className="row g-5 g-xl-5 align-items-start mu-invite__grid">
        <div className="col-xl-8">
          <div className="card mb-5 mu-invite__section-card">
            <div className="card-header border-bottom-dashed mu-invite__section-header">
              <div className="mu-invite__section-header-inner">
                <span className="mu-invite__step">
                 <i className="ki-duotone ki-user fs-2 text-danger">
                    <span className="path1"></span>
                    <span className="path2"></span>
                  </i>
                </span>
                <div>
                  <h3 className="mu-invite__section-title">User details</h3>
                  <p className="mu-invite__section-desc">Name, email, and permission level</p>
                </div>
              </div>
            </div>

            <div className="card-body mu-invite__section-body">
              {loadingUser ? (
                <div className="mu-invite__state">
                  <Spin />
                  <span className="fs-7">Loading user details…</span>
                </div>
              ) : (
                <>
                  <div className="row g-5">
                    <div className="col-md-6">
                      <label className="mu-invite__label" htmlFor="mu-invite-name">
                        Full name
                      </label>
                      <Input
                        id="mu-invite-name"
                        variant="filled"
                        size="middle"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className={`mu-invite__input ${isEdit ? "mu-invite__input--readonly" : ""}`}
                        disabled={isEdit}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="mu-invite__label" htmlFor="mu-invite-email">
                        Work email
                      </label>
                      <Input
                        id="mu-invite-email"
                        variant="filled"
                        size="middle"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className={`mu-invite__input ${isEdit ? "mu-invite__input--readonly" : ""}`}
                        disabled={isEdit}
                      />
                    </div>
                  </div>

                  <div className="separator my-5 separator-dashed" />

                  <div>
                    <label className="mu-invite__label mb-3">Select role</label>
                    <div className="mu-invite__roles" role="radiogroup" aria-label="Sub-user role">
                      {roles.map((r) => {
                        const Icon = ROLE_ICONS[r.key] || BarChartOutlined;
                        const active = role === r.key;
                        return (
                          <button
                            key={r.key}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            className={`mu-invite__role-card ${active ? "mu-invite__role-card--active" : ""}`}
                            onClick={() => handleRoleChange(r.key)}
                          >
                            {active ? (
                              <span className="mu-invite__role-check" aria-hidden>
                                <CheckOutlined />
                              </span>
                            ) : null}
                            <div className="d-flex flex-row" style={{ alignItems: "center" }}>
                              <span className="mu-invite__role-icon mb-0">
                                <Icon />
                              </span>
                              <div className="d-flex flex-column ms-3">
                                <span className="mu-invite__role-name fw-bold fs-6">{r.label}</span>
                                <span className="mu-invite__role-desc fs-7 text-muted">{r.description}</span>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="card mu-invite__section-card">
            <div className="card-header border-bottom-dashed mu-invite__section-header">
              <div className="mu-invite__section-header-inner mu-invite__section-header-inner--wide">
                <div className="d-flex align-items-center gap-3">
                  <span className="mu-invite__step">
                    <i className="ki-duotone ki-lock fs-2 text-danger">
                      <span className="path1"></span>
                      <span className="path2"></span>
                      <span className="path3"></span>
                    </i>
                  </span>
                  <div>
                    <h3 className="mu-invite__section-title">Assign accounts &amp; modules</h3>
                    <p className="mu-invite__section-desc">
                      Turn on accounts and choose what they can access
                    </p>
                  </div>
                </div>
                <span className="mu-invite__active-badge">{enabledCount} active</span>
              </div>
            </div>

            <div className="card-body mu-invite__section-body mu-invite__section-body--accounts">
              {isLoading ? (
                <div className="mu-invite__state">
                  <Spin />
                  <span className="fs-7">Loading seller accounts…</span>
                </div>
              ) : accountAccess.length === 0 ? (
                <div className="mu-invite__state mu-invite__state--empty">
                  <div className="mu-invite__empty-icon">
                    <i className="ki-outline ki-shop" />
                  </div>
                  <p className="fs-6 fw-bold mb-1">No seller accounts found</p>
                  <span className="fs-7 text-muted">Create a seller account first to assign access.</span>
                </div>
              ) : (
                <div className="mu-invite__accounts">
                  {accountAccess.map((acc) => {
                    const moduleCount = acc.modules?.length || 0;
                    const moduleBadge =
                      moduleCount === 1 ? "1 module" : `${moduleCount} modules`;

                    return (
                      <div
                        key={acc.account_id}
                        className={`mu-invite__account ${acc.enabled ? "mu-invite__account--on" : ""}`}
                      >
                        <div className="mu-invite__account-head">
                          <Switch
                            checked={acc.enabled}
                            onChange={(checked) => toggleAccount(acc.account_id, checked)}
                            className="mu-invite__switch"
                          />
                          <div className="mu-invite__account-info">
                            <span
                              className={`mu-invite__account-icon ${acc.enabled ? "mu-invite__account-icon--on" : "mu-invite__account-icon--off"}`}
                            >
                              {accountInitial(acc.account_name)}
                            </span>
                            <div className="min-w-0">
                              <div className="mu-invite__account-name text-truncate">{acc.account_name}</div>
                              <div className="mu-invite__account-meta text-truncate">
                                {[acc.marketplace, acc.category].filter(Boolean).join(" · ") || "Seller account"}
                              </div>
                            </div>
                          </div>
                          {acc.enabled ? (
                            <span className="mu-invite__modules-badge">{moduleBadge}</span>
                          ) : (
                            <span className="mu-invite__off-badge">Off</span>
                          )}
                        </div>

                        {acc.enabled ? (
                          <div className="mu-invite__modules-panel">
                            <div className="mu-invite__modules-toolbar">
                              <span className="mu-invite__modules-heading">
                                Modules this user can access
                              </span>
                              <button
                                type="button"
                                className="mu-invite__select-all"
                                onClick={() => selectAllModules(acc.account_id)}
                              >
                                Select all
                              </button>
                            </div>
                            <ModulePermissionTree
                              pages={pages}
                              selectedModules={acc.modules}
                              assignableKeys={assignableKeys}
                              onToggleModule={(moduleKey) => toggleModule(acc.account_id, moduleKey)}
                            />
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-xl-4 mu-invite__summary-col">
          <div className="mu-invite__summary-sticky">
            <div className="card mu-invite__section-card overflow-hidden">
              <div className="card-body p-0">
                <SubUserSummaryCard
                  name={name}
                  email={email}
                  role={role}
                  roleLabelMap={Object.fromEntries(roles.map((r) => [r.key, r.label]))}
                  moduleLabelMap={moduleLabelMap}
                  accountAccess={accountAccess}
                  submitting={submitting}
                  onSubmit={handleSubmit}
                  onCancel={onBack}
                  submitLabel={isEdit ? "Save changes" : "Send invite"}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
